export type OtaReleaseType = 'update-available' | 'update-now' | 'avatar-pack';
export type OtaChannel = 'canary' | 'early' | 'broad';
export type OtaDeviceScope = string[] | 'all';

interface OtaManifestBase {
  version: string;
  packageUrl: string;
  sha256: string;
  sizeBytes: number;
  releaseNotes?: string;
  channel: OtaChannel;
}

export type OtaRelease =
  | {
      type: 'update-available';
      deviceIds: OtaDeviceScope;
      manifest: OtaManifestBase & {
        kind: 'app';
        mandatory?: false;
        minFromVersion?: string;
        rollback?: false;
      };
    }
  | {
      type: 'update-now';
      deviceIds: OtaDeviceScope;
      manifest: OtaManifestBase & {
        kind: 'app';
        mandatory?: boolean;
        minFromVersion?: string;
        rollback?: false;
      };
    }
  | {
      type: 'avatar-pack';
      deviceIds: OtaDeviceScope;
      manifest: OtaManifestBase & {
        kind: 'avatar';
        mandatory?: boolean;
        minFromVersion?: string;
        rollback?: false;
      };
    }
  | {
      type: 'update-now';
      deviceIds: [string, ...string[]];
      manifest: OtaManifestBase & {
        kind: 'app';
        mandatory: true;
        minFromVersion?: never;
        rollback: true;
      };
    };

type OtaNonRollbackRelease = Exclude<OtaRelease, { manifest: { rollback: true } }>;
type OtaRollbackRelease = Extract<OtaRelease, { manifest: { rollback: true } }>;

export type OtaProposePayload =
  | { release: OtaNonRollbackRelease; allowRollback?: false; rollbackReason?: never }
  | { release: OtaRollbackRelease; allowRollback: true; rollbackReason: string };

export interface OtaPublishResult {
  type: OtaReleaseType;
  version: string;
  targets: number;
  attempted: number;
  accepted: number;
  queued: number;
  alreadyAccepted: number;
  delivered: number;
  applied: number;
  results: Array<{
    ok: boolean;
    applied: boolean;
    outcome: 'pending' | 'delivered' | 'no-channel' | 'write-failed' | 'partial' | 'already-accepted' | 'unsigned-dropped' | 'outbox-failed';
    deviceId: string;
    attemptedChannels: number;
    deliveredChannels: number;
    failedChannels: number;
    nonce: string;
  }>;
}

export interface OtaReleaseDraft {
  type: OtaReleaseType;
  version: string;
  packageUrl: string;
  sha256: string;
  sizeBytes: number;
  channel: OtaChannel;
  targetMode: 'all' | 'selected';
  selectedDeviceIds: string[];
  activeDeviceIds: string[];
}

export interface OtaDeviceTarget {
  deviceId: string;
  label: string;
}

const MAX_TARGETS = 500;

function canonicalDeviceIds(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));
}

export function activeOtaDeviceTargets(devices: Array<{
  deviceId: string;
  machineName?: string;
  purpose: 'user' | 'broker-audit';
  status: 'active' | 'revoked';
}>): OtaDeviceTarget[] {
  const labels = new Map<string, string>();
  for (const device of devices) {
    const deviceId = device.deviceId.trim();
    if (!deviceId || device.purpose !== 'user' || device.status !== 'active') continue;
    const label = device.machineName?.trim() ? `${device.machineName.trim()} (${deviceId})` : deviceId;
    if (!labels.has(deviceId)) labels.set(deviceId, label);
  }
  return [...labels].map(([deviceId, label]) => ({ deviceId, label }))
    .sort((left, right) => left.deviceId.localeCompare(right.deviceId));
}

export function buildOtaRelease(draft: OtaReleaseDraft): OtaNonRollbackRelease {
  const activeDeviceIds = canonicalDeviceIds(draft.activeDeviceIds);
  if (activeDeviceIds.length === 0 || activeDeviceIds.length > MAX_TARGETS) {
    throw new Error('OTA publish requires between 1 and 500 active user devices.');
  }

  let deviceIds: OtaDeviceScope = 'all';
  if (draft.targetMode === 'selected') {
    const selected = canonicalDeviceIds(draft.selectedDeviceIds);
    if (selected.length === 0 || selected.length > MAX_TARGETS) {
      throw new Error('Select between 1 and 500 active user devices.');
    }
    const active = new Set(activeDeviceIds);
    if (selected.some((deviceId) => !active.has(deviceId))) {
      throw new Error('OTA target selection contains an inactive or unknown device.');
    }
    deviceIds = selected;
  }

  const manifest = {
    version: draft.version.trim(),
    packageUrl: draft.packageUrl.trim(),
    sha256: draft.sha256.trim().toLowerCase(),
    sizeBytes: draft.sizeBytes,
    channel: draft.channel,
    mandatory: false as const,
    rollback: false as const,
  };
  if (draft.type === 'avatar-pack') {
    return { type: draft.type, deviceIds, manifest: { ...manifest, kind: 'avatar' } };
  }
  if (draft.type === 'update-available') {
    return { type: draft.type, deviceIds, manifest: { ...manifest, kind: 'app' } };
  }
  return { type: 'update-now', deviceIds, manifest: { ...manifest, kind: 'app' } };
}
